import { Invoice } from '@/interfaces/Invoice'

export type DocKind = 'invoice' | 'order' | 'expense'
export type Doc = Invoice

export const kindMeta: Record<DocKind, { plural: string; segment: string; api: string; counterparty: 'customer' | 'supplier'; counterpartyLabel: string }> = {
  invoice: { plural: 'Faktury', segment: 'invoice', api: 'invoices', counterparty: 'customer', counterpartyLabel: 'Odběratel' },
  order: { plural: 'Výzvy k platbě', segment: 'order', api: 'orders', counterparty: 'customer', counterpartyLabel: 'Odběratel' },
  expense: { plural: 'Náklady', segment: 'expense', api: 'expenses', counterparty: 'supplier', counterpartyLabel: 'Dodavatel' },
}

export const docTotal = (doc: Doc, field: 'totalPrice' | 'totalPriceTaxFree' = 'totalPrice') =>
  (doc.items ?? []).reduce((sum, item) => sum + Number(item[field]?.amount ?? 0), 0)

export const docCurrency = (doc: Doc) =>
  (doc as { currency?: string }).currency ?? doc.items?.[0]?.totalPrice?.currency ?? 'CZK'

export const counterpartyOf = (doc: Doc, kind: DocKind) => (kindMeta[kind].counterparty === 'supplier' ? doc.supplier : doc.customer)

export type StatusGroup = 'all' | 'open' | 'overdue' | 'paid' | 'cancelled'

export const statusGroup = (doc: Doc): Exclude<StatusGroup, 'all'> => {
  const s = doc.status
  if (s === 'cancelled' || s === 'refunded') return 'cancelled'
  if (s === 'paid' || s === 'overpaid' || doc.paidAt) return 'paid'
  if (s === 'overdue') return 'overdue'
  if (doc.deadlineAt && new Date(doc.deadlineAt) < startOfToday()) return 'overdue'
  return 'open'
}

/** Stav pro zobrazení: dopočítá po splatnosti i když API vrací issued. */
export const displayStatus = (doc: Doc): string => {
  const g = statusGroup(doc)
  if (g === 'overdue') return 'overdue'
  if (g === 'paid') return doc.status === 'overpaid' ? 'overpaid' : 'paid'
  return doc.status ?? 'issued'
}

export const statusLabel: Record<string, string> = {
  draft: 'Koncept',
  issued: 'Vystavená',
  sent: 'Odeslaná',
  paidPartially: 'Částečně zaplacená',
  paid: 'Zaplacená',
  overpaid: 'Přeplacená',
  overdue: 'Po splatnosti',
  cancelled: 'Zrušená',
  refunded: 'Vrácená',
}

export const startOfToday = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many)

/** "za 3 dny", "dnes", "5 dní po splatnosti". */
export const relativeDue = (deadline: string | undefined | null) => {
  if (!deadline) return null
  const d = new Date(deadline)
  d.setHours(0, 0, 0, 0)
  const diff = Math.round((d.getTime() - startOfToday().getTime()) / 86400000)
  if (diff === 0) return { text: 'splatná dnes', late: false }
  if (diff > 0) return { text: `za ${diff} ${plural(diff, 'den', 'dny', 'dní')}`, late: false }
  const n = Math.abs(diff)
  return { text: `${n} ${plural(n, 'den', 'dny', 'dní')} po splatnosti`, late: true }
}

export const formatShortDate = (date: string | undefined | null) =>
  date ? new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }).format(new Date(date)) : '-'

/** Popisek stavu podle typu dokladu (u nákladů jde o přijaté doklady). */
export const statusLabelFor = (kind: DocKind, status: string) => {
  if (kind === 'expense' && (status === 'issued' || status === 'sent')) return 'K úhradě'
  if (kind === 'order' && status === 'issued') return 'Vystavená'
  return statusLabel[status] ?? status
}

/** "1 doklad", "3 doklady", "12 dokladů" */
export const docsCount = (n: number) => `${n} ${n === 1 ? 'doklad' : n >= 2 && n <= 4 ? 'doklady' : 'dokladů'}`
